import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-notification';
import '@maersk-global/mds-components-core/mc-tag';
import { GeoIndex, Level, LEVEL_LABEL, LEVEL_LABEL_PLURAL, parseNodeKey, Redundancy } from '../../models/geography-tree.model';
import { GeographyService } from '../../services/geography.service';
import { ancestorPath, checkRedundancy, coveringAncestor, reconcileSelection, resolveToShops, searchGrouped } from './geo-index';

export type GeoFilterFit = 'small' | 'medium' | 'large';

/** A selected node, as rendered on a chip. */
export interface GeoFilterChip {
  key: string;
  level: Level;
  levelLabel: string;
  code: string;
  name: string;
  /** The already-selected ancestor that makes this chip a no-op, if any. */
  coveredBy: string | null;
  coveredByName: string;
}

/** A single typeahead result row. `rowIndex` is its position across all sections, for arrow keys. */
export interface GeoFilterRow {
  key: string;
  level: Level;
  code: string;
  name: string;
  path: string;
  shopsLabel: string;
  selected: boolean;
  covered: boolean;
  coveredByName: string;
  rowIndex: number;
}

export interface GeoFilterSectionView {
  level: Level;
  label: string;
  matchCount: number;
  hiddenCount: number;
  hiddenLabel: string;
  /** Name for the section's `role="group"` — carries the true count and any truncation. */
  ariaLabel: string;
  rows: GeoFilterRow[];
}

/** A redundancy or reconciliation message, with the one shortcut the user might have meant. */
export interface GeoFilterNotice {
  appearance: 'info' | 'warning';
  icon: string;
  message: string;
  action: { label: string; keys: string[] } | null;
  /** Removing any of these keys makes the notice stale. */
  dependsOn: string[];
}

let nextInstanceId = 0;

/**
 * A single control that takes a pick at **any** level of the geography hierarchy and outputs
 * **shops**. The whole accessible tree arrives in one call, so selection, search and redundancy
 * warnings all run locally — there is no network request while the user types.
 *
 * Works both inside and outside a form:
 *
 * ```html
 * <!-- Reactive / template forms — the control value is string[] -->
 * <app-aems-geo-filter formControlName="geography" [required]="true" (shopCodesChange)="onShops($event)" />
 *
 * <!-- No forms — two-way bind the selection, or just read the output -->
 * <app-aems-geo-filter [(selection)]="selection" (shopCodesChange)="onShops($event)" />
 * ```
 *
 * `selection` (node keys) is what you persist in a preset, a URL or a saved view; `shopCodes` is
 * derived for the request you are about to make and must never be stored. Storing the resolved shop
 * list would freeze a preset in time — a shop added to a region next month would never appear in a
 * saved "Asia Pacific" view.
 */
@Component({
  selector: 'app-aems-geo-filter',
  imports: [CommonModule],
  templateUrl: './aems-geo-filter.component.html',
  styleUrl: './aems-geo-filter.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => AemsGeoFilterComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => AemsGeoFilterComponent),
    },
  ],
})
export class AemsGeoFilterComponent implements OnInit, ControlValueAccessor, Validator {
  @Input() label: string = 'Geography';
  @Input() placeholder: string = 'Search region, cluster, country, location or shop';
  @Input() fit: GeoFilterFit = 'small';
  @Input() hint: string = '';
  @Input() hiddenlabel: boolean = false;
  @Input() width: string = '100%';

  /** Marks the field required. Applies as a form validator and as the field's own error state. */
  @Input()
  get required(): boolean {
    return this.requiredInput();
  }
  set required(value: boolean) {
    this.requiredInput.set(value);
    this.onValidatorChange();
  }

  /** Force the error state from outside, the way `mc-input` does. */
  @Input() set invalid(value: boolean) {
    this.invalidInput.set(value);
  }

  @Input() errormessage: string = '';

  @Input() set disabled(value: boolean) {
    this.disabledInput.set(value);
  }

  /**
   * The selection, for use without a form. Two-way bindable as `[(selection)]`. Inside a form the
   * same value flows through `formControlName` / `ngModel` instead.
   */
  @Input() set selection(value: string[] | null | undefined) {
    this.acceptSelection(value ?? []);
  }

  @Output() readonly selectionChange = new EventEmitter<string[]>();

  /** The resolved leaves — what you send with the request. Re-emitted whenever the tree lands. */
  @Output() readonly shopCodesChange = new EventEmitter<string[]>();

  /** The tree failed to load. The control renders its own retry affordance regardless. */
  @Output() readonly loadFailure = new EventEmitter<unknown>();

  @ViewChild('queryInput') private readonly queryInput?: ElementRef<HTMLInputElement>;

  private readonly geographyService = inject(GeographyService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly instanceId = `aems-geo-filter-${nextInstanceId++}`;
  readonly inputId = `${this.instanceId}-input`;
  readonly listboxId = `${this.instanceId}-listbox`;
  readonly noticeId = `${this.instanceId}-notice`;

  readonly index = signal<GeoIndex | null>(null);
  readonly loading = signal<boolean>(true);
  readonly loadFailed = signal<boolean>(false);
  readonly query = signal<string>('');
  readonly open = signal<boolean>(false);
  readonly focused = signal<boolean>(false);
  readonly activeIndex = signal<number>(-1);
  readonly notice = signal<GeoFilterNotice | null>(null);
  /** The chip briefly highlighted when the user re-picks something already selected. */
  readonly flashedKey = signal<string | null>(null);

  private readonly selectedKeys = signal<string[]>([]);
  private readonly requiredInput = signal<boolean>(false);
  private readonly invalidInput = signal<boolean>(false);
  private readonly disabledInput = signal<boolean>(false);
  private readonly disabledByForm = signal<boolean>(false);
  private readonly touched = signal<boolean>(false);

  private flashTimer: ReturnType<typeof setTimeout> | null = null;

  readonly isDisabled = computed<boolean>(() => this.disabledInput() || this.disabledByForm());

  readonly selectedSet = computed<ReadonlySet<string>>(() => new Set(this.selectedKeys()));

  readonly shopCodes = computed<string[]>(() => {
    const index = this.index();
    return index ? resolveToShops(index, this.selectedKeys()) : [];
  });

  /** Roughly 425 active users have no shops at all — that gets guidance, never an empty dropdown. */
  readonly noAccess = computed<boolean>(() => this.index()?.scope.shopCount === 0);

  readonly isUnrestricted = computed<boolean>(() => this.index()?.scope.isUnrestricted === true);

  /** The median user has exactly one shop: render it as a fixed label, not a picker with one option. */
  readonly singleShop = computed<{ code: string; name: string } | null>(() => {
    const index = this.index();
    if (!index) return null;

    const key = this.onlyShopKey(index);
    const entry = key ? index.byKey.get(key) : undefined;
    return entry ? { code: entry.node.code, name: entry.node.name } : null;
  });

  readonly chips = computed<GeoFilterChip[]>(() => {
    const index = this.index();
    if (!index) return [];

    // Recomputed from the current selection on every mutation, so removing one chip clears the
    // "covered" state of the others rather than leaving a stale flag behind.
    const selected = this.selectedSet();

    return this.selectedKeys().flatMap((key) => {
      const entry = index.byKey.get(key);
      if (!entry) return [];

      const covering = coveringAncestor(index, selected, key);
      return [
        {
          key,
          level: entry.level,
          levelLabel: LEVEL_LABEL[entry.level],
          code: entry.node.code,
          name: entry.node.name,
          coveredBy: covering,
          coveredByName: covering ? this.nameOf(index, covering) : '',
        },
      ];
    });
  });

  readonly sections = computed<GeoFilterSectionView[]>(() => {
    const index = this.index();
    const query = this.query();
    if (!index || query.trim().length === 0) return [];

    const selected = this.selectedSet();
    let cursor = 0;

    return searchGrouped(index, query).map((section) => {
      const rows = section.results.map<GeoFilterRow>((entry) => {
        const covering = coveringAncestor(index, selected, entry.key);
        return {
          key: entry.key,
          level: entry.level,
          code: entry.code,
          name: entry.name,
          path: ancestorPath(index, entry.key),
          shopsLabel: this.shopsLabel(index, entry.key),
          selected: selected.has(entry.key),
          covered: covering !== null,
          coveredByName: covering ? this.nameOf(index, covering) : '',
          rowIndex: cursor++,
        };
      });

      const hiddenCount = section.matchCount - rows.length;

      return {
        level: section.level,
        label: section.label,
        matchCount: section.matchCount,
        hiddenCount,
        hiddenLabel: LEVEL_LABEL_PLURAL[section.level].toLowerCase(),
        ariaLabel:
          hiddenCount > 0
            ? `${section.label}, ${section.matchCount} matches, showing ${rows.length}`
            : `${section.label}, ${section.matchCount} matches`,
        rows,
      };
    });
  });

  /** Every row in section order — arrow keys traverse this, skipping the headers. */
  readonly flatRows = computed<GeoFilterRow[]>(() => this.sections().flatMap((section) => section.rows));

  readonly showPopup = computed<boolean>(() => this.open() && !this.isDisabled() && this.query().trim().length > 0);

  readonly showError = computed<boolean>(
    () => this.invalidInput() || (this.required && this.touched() && this.selectedKeys().length === 0 && !this.loading()),
  );

  /** A getter, not a computed — `errormessage` and `label` are plain inputs, not signals. */
  get errorText(): string {
    return this.errormessage || `${this.label} is required`;
  }

  readonly activeOptionId = computed<string>(() => {
    const active = this.activeIndex();
    return this.showPopup() && active >= 0 ? this.optionId(active) : '';
  });

  constructor() {
    // Effects run after change detection, so the row for the new active index is already rendered.
    effect(() => {
      const active = this.activeIndex();
      if (active < 0 || !this.showPopup()) return;

      this.hostRef.nativeElement.querySelector<HTMLElement>(`[data-option-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
    });

    this.destroyRef.onDestroy(() => this.clearFlashTimer());
  }

  ngOnInit(): void {
    this.load(false);
  }

  // ---------------------------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------------------------

  /** Reload the tree. The control is unusable without it, hence a retry rather than a dead select. */
  retry(): void {
    this.load(true);
  }

  private load(force: boolean): void {
    this.loading.set(true);
    this.loadFailed.set(false);

    const request$ = force ? this.geographyService.refresh() : this.geographyService.getIndex();

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (index) => this.applyIndex(index),
      error: (error: unknown) => {
        this.loading.set(false);
        this.loadFailed.set(true);
        this.loadFailure.emit(error);
      },
    });
  }

  private applyIndex(index: GeoIndex): void {
    this.index.set(index);
    this.loading.set(false);
    this.loadFailed.set(false);

    let corrected = false;

    // A saved selection can reference a node that no longer exists. Drop it and say so.
    const { kept, dropped } = reconcileSelection(index, this.selectedKeys());
    if (dropped.length > 0) {
      this.selectedKeys.set(kept);
      this.notice.set(this.droppedNotice(index, dropped));
      corrected = true;
    }

    const onlyShop = this.onlyShopKey(index);
    if (onlyShop && this.selectedKeys().length === 0) {
      this.selectedKeys.set([onlyShop]);
      corrected = true;
    }

    if (corrected) this.emit();
    else this.shopCodesChange.emit(this.shopCodes());
  }

  // ---------------------------------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------------------------------

  /**
   * Adds the pick and, when it is redundant, explains why and offers the shortcut. The pick is never
   * blocked: silently dropping a click the user just made is the one behaviour that reads as broken,
   * and the resolved shop set is identical either way.
   */
  pick(row: GeoFilterRow): void {
    const index = this.index();
    if (!index || this.isDisabled()) return;

    const redundancy = checkRedundancy(index, this.selectedSet(), row.key);

    if (redundancy?.kind === 'already-selected') {
      this.flash(row.key);
      this.resetQuery();
      return;
    }

    this.selectedKeys.update((keys) => [...keys, row.key]);
    this.notice.set(this.redundancyNotice(index, redundancy));
    this.markTouched();
    this.resetQuery();
    this.emit();
  }

  removeKey(key: string): void {
    if (this.isDisabled()) return;

    this.selectedKeys.update((keys) => keys.filter((selected) => selected !== key));

    const notice = this.notice();
    if (notice?.dependsOn.includes(key)) this.notice.set(null);

    this.markTouched();
    this.emit();
  }

  clearAll(): void {
    if (this.isDisabled()) return;

    this.selectedKeys.set([]);
    this.notice.set(null);
    this.markTouched();
    this.emit();
    this.focusInput();
  }

  /** The unrestricted-user shortcut: every region in one action. */
  selectAllShops(): void {
    const index = this.index();
    if (!index || this.isDisabled()) return;

    this.selectedKeys.set([...(index.keysByLevel.get('region') ?? [])]);
    this.notice.set(null);
    this.markTouched();
    this.resetQuery();
    this.emit();
  }

  runNoticeAction(): void {
    const notice = this.notice();
    if (!notice?.action || this.isDisabled()) return;

    const drop = new Set(notice.action.keys);
    this.selectedKeys.update((keys) => keys.filter((key) => !drop.has(key)));
    this.notice.set(null);
    this.emit();
  }

  dismissNotice(): void {
    this.notice.set(null);
  }

  // ---------------------------------------------------------------------------------------------
  // Field interaction
  // ---------------------------------------------------------------------------------------------

  onQueryInput(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
    this.activeIndex.set(this.flatRows().length > 0 ? 0 : -1);
    this.open.set(true);
  }

  onFocus(): void {
    this.focused.set(true);
    if (this.query().trim().length > 0) this.open.set(true);
  }

  onKeydown(event: KeyboardEvent): void {
    const rows = this.flatRows();

    switch (event.key) {
      case 'ArrowDown':
        this.open.set(true);
        this.moveActive(1, rows.length);
        event.preventDefault();
        break;

      case 'ArrowUp':
        this.moveActive(-1, rows.length);
        event.preventDefault();
        break;

      case 'Home':
        if (rows.length > 0) {
          this.activeIndex.set(0);
          event.preventDefault();
        }
        break;

      case 'End':
        if (rows.length > 0) {
          this.activeIndex.set(rows.length - 1);
          event.preventDefault();
        }
        break;

      case 'Enter': {
        const row = rows[this.activeIndex()];
        if (row) {
          this.pick(row);
          event.preventDefault();
        }
        break;
      }

      case 'Escape':
        if (this.open()) {
          this.close();
          event.preventDefault();
        }
        break;

      case 'Backspace': {
        if (this.query().length > 0) break;

        const last = this.chips().at(-1);
        if (last) {
          this.removeKey(last.key);
          event.preventDefault();
        }
        break;
      }
    }
  }

  /** Chips are individually focusable and removable with Backspace / Delete. */
  onChipKeydown(event: KeyboardEvent, key: string): void {
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;

    this.removeKey(key);
    this.focusInput();
    event.preventDefault();
  }

  focusInput(): void {
    if (this.isDisabled()) return;
    this.queryInput?.nativeElement.focus();
  }

  optionId(rowIndex: number): string {
    return `${this.instanceId}-option-${rowIndex}`;
  }

  /**
   * Keeps focus in the query input when the pointer goes down inside the popup. Without this the
   * input blurs, `focusout` closes the popup, and the row is gone before its click can land.
   */
  @HostListener('mousedown', ['$event'])
  onHostMouseDown(event: MouseEvent): void {
    if (this.popupTarget(event)) event.preventDefault();
  }

  /**
   * Selection and click-to-focus are delegated here rather than bound per row. Result rows must stay
   * non-focusable — under `aria-activedescendant` the input keeps focus and Arrow keys plus Enter
   * drive selection, which `onKeydown` handles.
   */
  @HostListener('click', ['$event'])
  onHostClick(event: Event): void {
    const row = this.rowFromEvent(event);
    if (row) {
      this.pick(row);
      return;
    }

    if ((event.target as HTMLElement | null)?.closest('.geo-filter__field')) this.focusInput();
  }

  /** `mouseover` rather than `mouseenter`, because only the former bubbles up to the host. */
  @HostListener('mouseover', ['$event'])
  onHostMouseOver(event: Event): void {
    const row = this.rowFromEvent(event);
    if (row) this.activeIndex.set(row.rowIndex);
  }

  private popupTarget(event: Event): boolean {
    return (event.target as HTMLElement | null)?.closest('.geo-filter__popup') != null;
  }

  private rowFromEvent(event: Event): GeoFilterRow | undefined {
    const option = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-option-index]');
    if (!option) return undefined;

    return this.flatRows().at(Number(option.dataset['optionIndex']));
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: Event): void {
    if (!this.open()) return;
    if (this.hostRef.nativeElement.contains(event.target as Node)) return;

    this.close();
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (next && this.hostRef.nativeElement.contains(next)) return;

    this.focused.set(false);
    this.close();
    this.markTouched();
  }

  private close(): void {
    this.open.set(false);
    this.activeIndex.set(-1);
  }

  private resetQuery(): void {
    this.query.set('');
    if (this.queryInput) this.queryInput.nativeElement.value = '';
    this.close();
  }

  private moveActive(delta: number, total: number): void {
    if (total === 0) {
      this.activeIndex.set(-1);
      return;
    }

    const current = this.activeIndex();

    if (current < 0) {
      // Nothing active yet: enter the list from whichever end the user is heading towards.
      this.activeIndex.set(delta > 0 ? 0 : total - 1);
      return;
    }

    this.activeIndex.set((current + delta + total) % total);
  }

  private flash(key: string): void {
    this.clearFlashTimer();
    this.flashedKey.set(key);
    this.flashTimer = setTimeout(() => {
      this.flashedKey.set(null);
      this.flashTimer = null;
    }, 1200);
  }

  private clearFlashTimer(): void {
    if (this.flashTimer === null) return;
    clearTimeout(this.flashTimer);
    this.flashTimer = null;
  }

  // ---------------------------------------------------------------------------------------------
  // ControlValueAccessor / Validator
  // ---------------------------------------------------------------------------------------------

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  writeValue(value: string[] | null): void {
    this.acceptSelection(value ?? []);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm.set(isDisabled);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.required) return null;

    const value = (control.value as string[] | null) ?? [];
    return value.length === 0 ? { required: true } : null;
  }

  // ---------------------------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------------------------

  /** Applies a selection handed in from outside (form or `[selection]`) without echoing it back. */
  private acceptSelection(keys: string[]): void {
    const unique = [...new Set(keys)];

    // `[(selection)]` writes our own emission straight back at us — don't re-emit for it.
    const current = this.selectedKeys();
    if (unique.length === current.length && unique.every((key, i) => key === current[i])) return;

    const index = this.index();

    if (!index) {
      // The tree has not landed yet — hold the keys as given and reconcile in applyIndex.
      this.selectedKeys.set(unique);
      return;
    }

    const { kept, dropped } = reconcileSelection(index, unique);
    this.selectedKeys.set(kept);

    if (dropped.length > 0) {
      // What we hold is no longer what we were handed, so the form model has to be corrected.
      this.notice.set(this.droppedNotice(index, dropped));
      this.emit();
      return;
    }

    this.shopCodesChange.emit(this.shopCodes());
  }

  private emit(): void {
    const keys = this.selectedKeys();
    this.onChange(keys);
    this.selectionChange.emit(keys);
    this.shopCodesChange.emit(this.shopCodes());
  }

  private markTouched(): void {
    if (this.touched()) return;

    this.touched.set(true);
    this.onTouched();
  }

  private onlyShopKey(index: GeoIndex): string | null {
    const shops = index.keysByLevel.get('shop') ?? [];
    return index.scope.shopCount === 1 && shops.length === 1 ? shops[0] : null;
  }

  private nameOf(index: GeoIndex, key: string): string {
    return index.byKey.get(key)?.node.name ?? parseNodeKey(key).code;
  }

  /**
   * A scoped user's branch can hold fewer shops than it really has, so a bare count would imply a
   * completeness the data does not have.
   */
  private shopsLabel(index: GeoIndex, key: string): string {
    if (parseNodeKey(key).level === 'shop') return '';

    const count = index.shopsUnder.get(key)?.length ?? 0;
    const noun = count === 1 ? 'shop' : 'shops';
    return index.scope.isUnrestricted ? `${count} ${noun}` : `${count} ${noun} you can access`;
  }

  private redundancyNotice(index: GeoIndex, redundancy: Redundancy): GeoFilterNotice | null {
    if (!redundancy) return null;

    if (redundancy.kind === 'covered-by') {
      const level = LEVEL_LABEL[parseNodeKey(redundancy.incoming).level].toLowerCase();
      return {
        appearance: 'info',
        icon: 'info-circle',
        message: `${this.nameOf(index, redundancy.incoming)} is already covered by ${this.nameOf(index, redundancy.by)}.`,
        action: { label: `Narrow to this ${level}`, keys: [redundancy.by] },
        dependsOn: [redundancy.incoming, redundancy.by],
      };
    }

    if (redundancy.kind === 'supersedes') {
      const count = redundancy.replaces.length;
      return {
        appearance: 'info',
        icon: 'info-circle',
        message: `${this.nameOf(index, redundancy.incoming)} covers ${count} ${this.nounFor(redundancy.replaces)} you already picked.`,
        action: { label: `Remove the ${count}`, keys: [...redundancy.replaces] },
        dependsOn: [redundancy.incoming, ...redundancy.replaces],
      };
    }

    return null;
  }

  private droppedNotice(index: GeoIndex, dropped: string[]): GeoFilterNotice {
    const count = dropped.length;
    const verb = count === 1 ? 'is' : 'are';
    const removed = count === 1 ? 'was' : 'were';

    return {
      appearance: 'warning',
      icon: 'exclamation-triangle',
      message: `${count} ${this.nounFor(dropped)} in this filter ${verb} no longer active and ${removed} removed.`,
      action: null,
      dependsOn: [...dropped],
    };
  }

  /** "3 shops" when every key sits at one level, "3 selections" when they are mixed. */
  private nounFor(keys: readonly string[]): string {
    const levels = new Set(keys.map((key) => parseNodeKey(key).level));

    if (levels.size !== 1) return keys.length === 1 ? 'selection' : 'selections';

    const level = [...levels][0];
    return (keys.length === 1 ? LEVEL_LABEL[level] : LEVEL_LABEL_PLURAL[level]).toLowerCase();
  }
}
